import { useEffect, useRef, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { Check, Package, X, Loader2 } from "lucide-react";
import api from "../lib/api";
import { useLocale } from "../contexts/LocaleContext";

const MAX_POLLS = 8;
const POLL_INTERVAL = 2000;

export default function OrderConfirmation() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [o, setO] = useState(null);
  const [polling, setPolling] = useState(!!sessionId);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [pollError, setPollError] = useState(null);
  const attempts = useRef(0);
  const { fmt } = useLocale();

  const fetchOrder = () => api.get(`/orders/${id}`).then((r) => setO(r.data)).catch(() => {});

  useEffect(() => {
    fetchOrder();
  }, [id]);

  // Poll Stripe checkout status when redirected back
  useEffect(() => {
    if (!sessionId) return;
    let stopped = false;
    const poll = async () => {
      if (stopped) return;
      attempts.current += 1;
      try {
        const r = await api.get(`/checkout/status/${sessionId}`);
        setPaymentStatus(r.data.payment_status);
        if (r.data.payment_status === "paid") {
          setPolling(false);
          await fetchOrder();
          return;
        }
        if (r.data.status === "expired" || (r.data.payment_status === "unpaid" && r.data.status === "complete")) {
          setPolling(false);
          await fetchOrder();
          return;
        }
        if (attempts.current >= MAX_POLLS) {
          setPolling(false);
          setPollError("Still verifying — refresh in a moment or check your email.");
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      } catch (e) {
        if (attempts.current >= MAX_POLLS) {
          setPolling(false);
          setPollError("Could not verify payment status — please contact us if the charge appears on your card.");
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      }
    };
    poll();
    return () => { stopped = true; };
  }, [sessionId]);

  if (!o) return <div className="py-32 text-center text-gray-500">Loading…</div>;

  const isPaid = o.payment_status === "paid";
  const isFailed = o.payment_status === "failed";
  const isPending = o.payment_status === "pending" || polling;

  return (
    <div className="max-w-2xl mx-auto px-6 py-20 text-center" data-testid="order-confirmation">
      {polling && (
        <div className="bg-[#FAF7F2] border border-copper/30 px-5 py-4 mb-8 flex items-center gap-3" data-testid="payment-verifying">
          <Loader2 size={18} className="text-copper animate-spin"/>
          <div className="text-left">
            <div className="text-sm text-dark font-semibold">Verifying your payment…</div>
            <div className="text-xs text-gray-600">This usually takes a few seconds. Please don&apos;t close this tab.</div>
          </div>
        </div>
      )}

      <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto ${isPaid ? "bg-copper text-white" : isFailed ? "bg-red-500 text-white" : "bg-gray-200 text-gray-500"}`}>
        {isPaid ? <Check size={28}/> : isFailed ? <X size={28}/> : <Loader2 size={28} className="animate-spin"/>}
      </div>

      <div className="overline mt-6 mb-2">{isPaid ? "Order Confirmed" : isFailed ? "Payment Failed" : "Order Pending"}</div>
      <h1 className="font-display text-5xl text-dark">
        {isPaid ? "Thank You" : isFailed ? "Payment didn't go through" : "Almost there…"}
      </h1>
      <p className="text-gray-600 mt-4">
        Order <span className="text-copper">{o.order_number}</span>
        {isPaid && <> — confirmation sent to <span className="text-dark">{o.customer_email}</span></>}
        {isFailed && <> — you can try again or pick a different payment method.</>}
      </p>

      {pollError && (
        <div className="mt-6 text-sm text-red-600" data-testid="poll-error">{pollError}</div>
      )}

      <div className="card-luxe p-6 mt-10 text-left">
        <div className="flex items-center justify-between mb-3">
          <div className="overline">Items</div>
          <span className={`text-[11px] uppercase tracking-luxe px-2 py-0.5 ${isPaid ? "bg-green-50 text-green-700" : isFailed ? "bg-red-50 text-red-700" : "bg-gray-100 text-gray-600"}`} data-testid="order-payment-status">
            {o.payment_status}
          </span>
        </div>
        {o.items.map((i, idx) => <div key={idx} className="flex justify-between text-sm py-1"><span>{i.name} ×{i.quantity}</span><span>{fmt(i.subtotal)}</span></div>)}
        <div className="flex justify-between font-display text-xl pt-4 mt-4 border-t border-black/5"><span>Total</span><span className="text-copper">{fmt(o.total)}</span></div>
      </div>

      <div className="flex gap-3 justify-center mt-8 flex-wrap">
        {isFailed ? (
          <Link to="/checkout" className="btn-primary"><Package size={14}/> Try Again</Link>
        ) : (
          <Link to="/track" className="btn-outline"><Package size={14}/> Track Order</Link>
        )}
        <Link to="/shop" className={isFailed ? "btn-outline" : "btn-primary"}>Continue Shopping</Link>
      </div>
    </div>
  );
}
